/**
 * NotificationContext.js — Notification Engine & Settings
 *
 * Manages backend-driven notifications (badge count + paginated list),
 * plus app-wide settings (notifSettings, currencySettings, orderConfig)
 * and the ephemeral toast queue.
 *
 * Usage:
 *   const { unreadCount, notifications, ... } = useNotifications(currentUser, authToken);
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { fleetApi } from '../api/fleetApi';
import { DEFAULT_CURRENCY_SETTINGS, DEFAULT_ORDER_CONFIG } from '../config/defaults';

function playNotificationSound() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.type = 'sine';
    osc.frequency.setValueAtTime(880, ctx.currentTime);
    osc.frequency.exponentialRampToValueAtTime(440, ctx.currentTime + 0.15);
    gain.gain.setValueAtTime(0.3, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.3);
    osc.start(ctx.currentTime);
    osc.stop(ctx.currentTime + 0.3);
  } catch {}
}

// ── Default settings constants ─────────────────────────────────────────────────

export const DEFAULT_NOTIF_SETTINGS = {
  compliance: {
    driverLicenseExpiry:       { enabled: true, daysBefore: [30, 15, 7] },
    vehicleRegistrationExpiry: { enabled: true, daysBefore: [30, 15, 7] },
    vehicleInsuranceExpiry:    { enabled: true, daysBefore: [30, 15, 7] },
    vehicleInspectionExpiry:   { enabled: true, daysBefore: [30, 15, 7] },
    driverMedicalExpiry:       { enabled: true, daysBefore: [30, 15, 7] },
  },
  maintenance: {
    preventiveMaintenance: { enabled: true, everyKm: 10000 },
    oilChange:             { enabled: true, everyKm: 5000 },
    tireReplacement:       { enabled: true, everyKm: 40000 },
  },
  operational: {
    newOrderCreated:   { enabled: true },
    orderAssigned:     { enabled: true },
    orderDelayed:      { enabled: true },
    deliveryCompleted: { enabled: true },
    deliveryFailed:    { enabled: true },
    orderStatusChange: { enabled: true },
  },
};

// ── localStorage helpers ───────────────────────────────────────────────────────

function loadNotifSettings() {
  try {
    const stored = localStorage.getItem('fleet_notif_settings');
    return stored ? { ...DEFAULT_NOTIF_SETTINGS, ...JSON.parse(stored) } : DEFAULT_NOTIF_SETTINGS;
  } catch { return DEFAULT_NOTIF_SETTINGS; }
}

function saveNotifSettingsToStorage(settings) {
  localStorage.setItem('fleet_notif_settings', JSON.stringify(settings));
}

function loadCurrencySettings() {
  try {
    const stored = localStorage.getItem('fleet_currency_settings');
    return stored ? JSON.parse(stored) : DEFAULT_CURRENCY_SETTINGS;
  } catch { return DEFAULT_CURRENCY_SETTINGS; }
}

function saveCurrencySettingsToStorage(settings) {
  localStorage.setItem('fleet_currency_settings', JSON.stringify(settings));
}

function loadOrderConfig() {
  try {
    const s = localStorage.getItem('fleet_order_config');
    return s ? JSON.parse(s) : DEFAULT_ORDER_CONFIG;
  } catch { return DEFAULT_ORDER_CONFIG; }
}

function saveOrderConfigToStorage(cfg) {
  localStorage.setItem('fleet_order_config', JSON.stringify(cfg));
}

// ── Map API notification shape to frontend shape ───────────────────────────────

function normalizeSeverity(raw) {
  if (!raw && raw !== 0) return 'info';
  const s = String(raw).toLowerCase();
  if (s === '1' || s === 'info' || s === 'information') return 'info';
  if (s === '2' || s === 'warning' || s === 'warn')     return 'warning';
  if (s === '3' || s === 'danger'  || s === 'error')    return 'danger';
  if (s === '4' || s === 'success')                     return 'success';
  return 'info';
}

/** Geofence crossings arrive as GeofenceEnter / GeofenceExit. */
const GEOFENCE_TYPE = /^Geofence(Enter|Exit)$/i;

function mapNotif(item) {
  const type = item.type || item.notificationType || '';
  return {
    id:        item.id,
    title:     item.title     || '',
    message:   item.message   || item.body || '',
    // Kept as its own field now. It used to be folded into `category` and lost,
    // which meant an Enter could not be told from an Exit once mapped — the one
    // distinction a geofence alert exists to make.
    type,
    category:  item.category  || (GEOFENCE_TYPE.test(type) ? 'geofence' : type) || 'info',
    severity:  normalizeSeverity(item.severity ?? item.level),
    timestamp: item.timestamp || item.createdAt || new Date().toISOString(),
    read:      item.isRead    ?? item.read ?? false,
    // What the alert is about, so it can be opened rather than just read. The
    // server has always sent these; this mapper simply used to discard them.
    relatedEntityType: item.relatedEntityType || null,
    relatedEntityId:   item.relatedEntityId ?? null,
  };
}

// ── Constants ─────────────────────────────────────────────────────────────────

const TAKE = 20;
const POLL_INTERVAL = 30_000;

// ── Custom hook ────────────────────────────────────────────────────────────────

/**
 * useNotifications — manages all notification and settings state.
 *
 * @param {object} currentUser - The logged-in user.
 * @param {string} authToken   - Bearer token for API calls.
 */
export function useNotifications(currentUser, authToken, api) {
  const [notifSettings,    setNotifSettings]    = useState(() => loadNotifSettings());
  const [currencySettings, setCurrencySettings] = useState(() => loadCurrencySettings());
  const [orderConfig,      setOrderConfig]      = useState(() => loadOrderConfig());
  const [showNotifPanel,   setShowNotifPanel]   = useState(false);
  const [toastQueue,       setToastQueue]       = useState([]);

  // ── Backend notification state ─────────────────────────────────────────────
  const [unreadCount,   setUnreadCount]   = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [hasMore,       setHasMore]       = useState(false);
  const [listLoading,   setListLoading]   = useState(false);
  const skipRef = useRef(0);
  const prevUnreadRef = useRef(null);

  // ── Poll unread count ──────────────────────────────────────────────────────
  useEffect(() => {
    if (!authToken) return;

    const poll = () => {
      fleetApi.notifications.getUnreadCount(authToken)
        .then(res => {
          const count = res?.count ?? (typeof res === 'number' ? res : 0);
          if (prevUnreadRef.current !== null && count > prevUnreadRef.current) {
            playNotificationSound();
          }
          prevUnreadRef.current = count;
          setUnreadCount(count);
        })
        .catch(() => {});
    };

    poll();
    const id = setInterval(poll, POLL_INTERVAL);
    return () => clearInterval(id);
    // currentUser?.id included as a reset trigger so the poll restarts on user switch
  }, [currentUser?.id, authToken]);

  // ── Load paginated notifications ───────────────────────────────────────────
  const loadNotifications = useCallback(async (reset = false) => {
    if (!authToken) return;
    const skip = reset ? 0 : skipRef.current;
    if (reset) setListLoading(true);
    try {
      const raw = await fleetApi.notifications.getList(skip, TAKE, authToken);
      const items = (Array.isArray(raw) ? raw : raw?.items ?? raw?.data ?? []).map(mapNotif);
      setNotifications(prev => reset ? items : [...prev, ...items]);
      skipRef.current = skip + items.length;
      setHasMore(items.length === TAKE);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    } finally {
      if (reset) setListLoading(false);
    }
  }, [authToken]);

  // ── Open panel (reset + fetch first page) ─────────────────────────────────
  const openPanel = useCallback(() => {
    setShowNotifPanel(true);
    skipRef.current = 0;
    loadNotifications(true);
  }, [loadNotifications]);

  // ── Mark single notification as read ──────────────────────────────────────
  const markRead = useCallback(async (id) => {
    // Optimistic update
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
    try {
      await fleetApi.notifications.markRead(id, authToken);
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      // Revert on failure
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: false } : n));
      setUnreadCount(prev => prev + 1);
    }
  }, [authToken]);

  // ── Mark all visible notifications as read ────────────────────────────────
  const markAllRead = useCallback(async () => {
    const unreadIds = notifications.filter(n => !n.read).map(n => n.id);
    if (!unreadIds.length) return;
    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
    // Fire-and-forget individual PATCHes (no bulk endpoint)
    unreadIds.forEach(id => {
      fleetApi.notifications.markRead(id, authToken).catch(() => {});
    });
  }, [notifications, authToken]);

  // ── Delete a single notification ──────────────────────────────────────────
  const deleteNotification = useCallback(async (id) => {
    setNotifications(prev => {
      const target = prev.find(n => n.id === id);
      if (target && !target.read) setUnreadCount(c => Math.max(0, c - 1));
      return prev.filter(n => n.id !== id);
    });
    try {
      await fleetApi.notifications.deleteNotif(id, authToken);
    } catch (err) {
      console.error('Failed to delete notification:', err);
      loadNotifications(true);
    }
  }, [authToken, loadNotifications]);

  // ── Load currency settings from API on mount ──────────────────────────────
  useEffect(() => {
    if (!authToken || !currentUser?.organizationId || !api?.currencies) return;
    api.currencies.getAll(currentUser.organizationId, authToken)
      .then(apiCurrencies => {
        if (!Array.isArray(apiCurrencies) || apiCurrencies.length === 0) return;
        const base = apiCurrencies.find(c => c.isBase) || apiCurrencies[0];
        const merged = {
          baseCurrency: base.code,
          currencies: apiCurrencies.map(c => ({
            id:        c.id,
            code:      c.code,
            name:      c.name,
            rateToUsd: c.rateToUsd,
            isActive:  c.isActive,
          })),
        };
        saveCurrencySettingsToStorage(merged);
        setCurrencySettings(merged);
      })
      .catch(() => {});
  }, [authToken, currentUser?.organizationId, api]);

  // ── Settings helpers ───────────────────────────────────────────────────────
  const saveAndSetNotifSettings    = (s)   => { saveNotifSettingsToStorage(s);    setNotifSettings(s);    };
  const saveAndSetCurrencySettings = async (s) => {
    saveCurrencySettingsToStorage(s);
    setCurrencySettings(s);
    if (!api?.currencies || !authToken || !currentUser?.organizationId) return;
    /* THE CATCHES HERE ARE WHY NOBODY NOTICED.
       Every call below was failing — `update` posted a body missing the
       server's [Required] Description and was rejected 400, and `setBase`
       pointed at a route that does not exist — and all three `.catch(() => {})`
       swallowed it whole. The local copy saved to localStorage, so the app
       looked right until the next machine or the next browser.
       The mapper and the route are fixed; the failure is now reported rather
       than hidden. It stays non-throwing, because the localStorage write above
       has already succeeded and the app must not lose that. */
    const orgId = currentUser.organizationId;
    const failures = [];
    const guard = (label, p) => p.catch((e) => { failures.push(`${label}: ${e?.message || 'failed'}`); });
    const updates = (s.currencies || []).map(c =>
      guard(c.code || 'currency', api.currencies.update({ ...c, organizationId: orgId }, orgId, authToken)));
    const base = (s.currencies || []).find(c => c.code === s.baseCurrency);
    if (base?.code) {
      updates.push(guard('base currency', api.currencies.setBase(base.code, orgId, authToken)));
    }
    await Promise.all(updates);
    if (failures.length) {
      // eslint-disable-next-line no-console
      console.warn('[currency settings] saved locally but not on the server —', failures.join('; '));
    }
  };
  const saveAndSetOrderConfig      = (cfg) => { saveOrderConfigToStorage(cfg);    setOrderConfig(cfg);    };

  // ── Toast helpers ──────────────────────────────────────────────────────────
  // Memoised like every other callback in this file. These are passed down as
  // props and listed as effect dependencies across the app; recreating them on
  // each render meant any effect that depended on them honestly would re-run
  // forever, so callers were omitting them from dependency arrays instead.
  // Both only use the state setter, which React guarantees is stable.
  const addToast     = useCallback((toast) => setToastQueue(q => [...q, toast]), []);
  const dismissToast = useCallback((id)    => setToastQueue(q => q.filter(t => t.id !== id)), []);

  return {
    unreadCount,
    notifications,        hasMore,      listLoading,
    loadNotifications,    markRead,     markAllRead,   deleteNotification,
    showNotifPanel,       setShowNotifPanel,   openPanel,
    toastQueue,           addToast,     dismissToast,
    notifSettings,        saveAndSetNotifSettings,
    currencySettings,     saveAndSetCurrencySettings,
    orderConfig,          saveAndSetOrderConfig,
  };
}
